/** Pure maths behind the Income Tax Calculator (US mode), so it can be tested without the page. Federal formulas unchanged. */
import { BRACKETS, STANDARD_DEDUCTION, bracketTax, marginalRate, employeeFica, selfEmploymentTax, qbiDeduction, type Filing, type Qbi } from "./usTax";
import { stateTax, statePayrollTax, type StateTaxResult, type StatePayrollResult } from "./stateTaxData";

export interface IncomeTaxInput {
  gross: number; pre: number; filing: Filing; selfEmployed: boolean;
  /** State code, "NONE" or "CUSTOM". */
  stateCode: string; customStatePct?: number;
}
export interface IncomeTaxOutput {
  std: number; taxable: number; fedTax: number; payroll: number; stateAmt: number; stateRes: StateTaxResult | null;
  /** State payroll tax withheld from employee wages (California SDI); null when none applies. Included in totalTax. */
  statePayroll: StatePayrollResult | null;
  totalTax: number; net: number; effective: number; marginal: number;
  /** Qualified business income deduction taken off taxable income (self-employed only; null otherwise). */
  qbi: Qbi | null;
  se: ReturnType<typeof selfEmploymentTax> | null; fica: ReturnType<typeof employeeFica> | null;
}

export function computeIncomeTax(i: IncomeTaxInput): IncomeTaxOutput {
  const { gross, filing } = i;
  const pre = Math.min(Math.max(0, i.pre), gross);
  const std = STANDARD_DEDUCTION[filing];
  const se = i.selfEmployed ? selfEmploymentTax(gross, filing) : null;
  const fica = i.selfEmployed ? null : employeeFica(gross, filing);
  const payroll = se ? se.total : fica!.total;
  const agi = Math.max(0, gross - pre - (se?.deductibleHalf ?? 0));
  const qbi = se ? qbiDeduction(gross, se.deductibleHalf, agi - std, filing) : null;
  const taxable = Math.max(0, agi - std - (qbi?.amount ?? 0));
  const fedTax = bracketTax(taxable, BRACKETS[filing]);
  // States start from federal adjusted gross income, so the deductible half of self-employment tax lowers the state base too.
  const stateBase = Math.max(0, gross - pre - (se?.deductibleHalf ?? 0));
  const stateRes = i.stateCode === "CUSTOM" || i.stateCode === "NONE" ? null : stateTax(i.stateCode, stateBase, filing, std);
  const stateAmt = i.stateCode === "NONE" ? 0 : stateRes ? stateRes.tax : stateBase * ((i.customStatePct ?? 0) / 100);
  // Employee wages only: self-employment profit pays no SDI. Charged on gross wages (a 401(k) deferral does not reduce it).
  const statePayroll = i.selfEmployed ? null : statePayrollTax(i.stateCode, gross);
  const totalTax = fedTax + stateAmt + payroll + (statePayroll?.amount ?? 0);
  const net = gross - totalTax - pre;
  const effective = gross > 0 ? (totalTax / gross) * 100 : 0;
  const marginal = marginalRate(taxable, BRACKETS[filing]) * 100;
  return { std, taxable, fedTax, payroll, stateAmt, stateRes, statePayroll, totalTax, net, effective, marginal, qbi, se, fica };
}

/** Pay periods per year for the per-paycheck view. */
export const INCOME_PERIODS = { year: 1, month: 12, semimonthly: 24, biweekly: 26, weekly: 52 } as const;
export type IncomePeriod = keyof typeof INCOME_PERIODS;
export const INCOME_PERIOD_LABEL: Record<IncomePeriod, string> = { year: "Year", month: "Month", semimonthly: "Semi-monthly paycheck (24 a year)", biweekly: "Biweekly paycheck (26 a year)", weekly: "Weekly paycheck (52 a year)" };
/** An annual amount shown for one period. */
export const perPeriod = (annual: number, period: IncomePeriod): number => annual / INCOME_PERIODS[period];
